import { ChevronLeftIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { InputType } from 'config/input.type.constants'
import { IButtonType, taskInputs } from 'pages/Admin/ConditionsAndTemplates'
import { EmailAssigneeOptions, TaskAssigneeOptions } from 'pages/Tasks/constants'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { loanTypeMap } from 'reducers/loanDetail.reducer'
import { getEmailTemplatesGroup, getOriginalTemplate, submitTask } from 'services'
import { Button, FormTable, Modal } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { getEmailHtml } from 'utils/emailHtml'
import { RenderInput } from 'utils/RenderInput'

import { defaultInputs, emailInputs, includingInfoByAction } from './constants'
import { ActionType, ILoanSubmissionActionConfig, isActionTypeHasStatusTo } from './types'

export const LoanSubmissionDetails = ({
  actionType,
  action,
  label,
  data,
  adminEmails,
  onBack,
  tempTasks,
}: {
  actionType: ActionType
  action: string
  label: string
  data: ILoanSubmissionActionConfig
  adminEmails: string[]
  onBack: Function
  tempTasks: Array<any>
}) => {
  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])
  const [tasks, setTasks] = useState(Array<any>)
  const [emails, setEmails] = useState<Array<any>>([])
  const [inputs, setInputs] = useState(defaultInputs(loanGlobalStatus))
  const [tInputs, setTInputs] = useState<Record<string, any>>({})
  const [eInputs, setEInputs] = useState<Record<string, any>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [emailTemplates, setEmailTemplates] = useState<any[]>([])
  const [showModal, setShowModal] = useState(false)
  const [previewData, setPreviewData] = useState<Record<string, any> | null>({})
  const [originalTemplate, setOriginalTemplate] = useState('')

  useEffect(() => {
    setIsLoading(true)
    getEmailTemplatesGroup('loanAction').then((emails: any) => {
      setIsLoading(false)
      setEmailTemplates(emails)
      let temp: any = cloneDeep(inputs)
      Object.keys(temp).forEach((key) => {
        const value = (data as any)[key]
        temp[key].value = value
      })

      if (!isActionTypeHasStatusTo(actionType)) {
        ;['section1', 'statusTo', 'statusToRequireVerify', 'statusListBeforeToUpdate'].forEach(
          (key) => (temp[key].visible = false),
        )
      }
      setInputs(temp)
    })
    getOriginalTemplate().then((res) => {
      setOriginalTemplate(res)
    })
  }, [action])

  useEffect(() => {
    let temp: any = emailInputs()
    const options: Record<number, string> = {}
    emailTemplates.forEach((item: any) => (options[item.id] = item.subject))
    temp['type'].options = options
    temp['emailTo'].options = assigneeOptions
    temp['includes'].options = includingInfoByAction(actionType)
    if (actionType == ActionType.UserAccounts) temp['channels'].visible = false
    temp['links'].render = renderECustomInput
    setEInputs(temp)
  }, [emailTemplates])

  useEffect(() => {
    setTasks(data.tasks)
    if (data.emails)
      data.emails.forEach((email) => {
        if (Number(email.type) == email.type) return
        const emailId = emailTemplates.find((v) => v.subject == email.type)?.id
        if (emailId) email.type = `${emailId}`
      })
    setEmails(data.emails)
  }, [data])

  const assigneeOptions = useMemo(() => {
    const options = { ...EmailAssigneeOptions(actionType) }
    adminEmails.forEach((email) => (options[email] = email))
    return options
  }, [adminEmails, actionType])

  const taskOptions = useMemo(() => {
    const options = { ...TaskAssigneeOptions() }
    adminEmails.forEach((email) => (options[email] = email))
    return options
  }, [adminEmails])

  useEffect(() => {
    let obj: any = {},
      IDObj: any = {
        0: 0,
      }
    tempTasks.map((task) => {
      obj[`${task.no}`] = `${task.description} - ${task.no}`
    })
    tasks.map((task) => {
      IDObj[task.id] = task.id
    })
    let temp: any = taskInputs()
    temp = {
      parentID: {
        title: 'Parent ID',
        inputType: 'select',
        hasDefaultOption: true,
        value: 0,
        options: IDObj,
        error: '',
        required: true,
        span: 2,
      },
      taskTemplateNo: {
        title: 'Choose Template',
        inputType: 'select',
        hasDefaultOption: true,
        options: obj,
        error: '',
        sort: true,
        sortKey: 'value',
      },
      ...temp,
      channels: {
        inputType: 'multiselect',
        title: 'Channel',
        options: loanTypeMap,
        defaultSelected: true,
      },
    }
    temp['assignedTo'].options = taskOptions
    temp['assignedToManager'].options = taskOptions
    setTInputs(temp)
  }, [tempTasks, taskOptions, tasks])

  const onChange = async (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    setInputs(newInputs)
  }

  const onSubmit = () => {
    const newInputs = cloneDeep(inputs)
    let values: Record<string, any> = {}
    let hasError = false
    Object.keys(inputs).forEach((key) => {
      if (inputs[key].visible === false) return null
      newInputs[key].error = InputValidate(newInputs[key])
      let { value, error } = newInputs[key]
      if (error) hasError = true
      values[key] = value
    })
    setInputs(newInputs)
    if (hasError) return false
    values.tasks = tasks
    values.emails = emails
    onBack(values)
  }

  const onSubmitEmails = (currentId: any, values: Record<string, any>) => {
    return new Promise(async (resolve) => {
      let temp = cloneDeep(emails)
      if (!currentId) {
        values = {
          id: Date.now(),
          ...values,
        }
        temp.push(values)
      } else {
        temp.map((item, index) => {
          if (item.id == currentId) {
            temp[index] = { ...temp[index], ...values }
          }
        })
      }
      setEmails(temp)
      resolve(values)
    })
  }

  const onRemoveEmails = async (id: any) => {
    return new Promise(async (resolve) => {
      let temp: any = []
      emails.map((item) => {
        if (item.id != id) temp.push(item)
      })
      setEmails(temp)
      resolve(true)
    })
  }

  const onSubmitTasks = (currentId: any, values: Record<string, any>) => {
    return new Promise(async (resolve) => {
      let temp = cloneDeep(tasks)
      if (!currentId) {
        let ID = 1
        tasks.map((task) => {
          if (ID <= task.id) ID = task.id + 1
        })
        values = {
          id: ID,
          ...values,
        }
        if (values.taskTemplateNo) {
          temp.push(values)
        } else {
          setIsLoading(true)
          delete values.taskTemplateNo
          try {
            const res = await submitTask(0, values)
            setIsLoading(false)
            if (res.taskNo) {
              values.taskTemplateNo = res.taskNo
              temp.push(values)
            }
          } catch {
            setIsLoading(false)
          }
        }
      } else {
        temp.map((item, index) => {
          if (item.id == currentId) {
            if (item.id == values.parentID) toast(`Invalid Parent ID`, { type: 'error' })
            else temp[index] = { ...temp[index], ...values }
          }
        })
      }
      setTasks(temp)
      resolve(values)
    })
  }

  const onRemoveTasks = async (id: any) => {
    return new Promise(async (resolve) => {
      let temp: any = []
      let hasChild = false
      tasks.map((item) => {
        if (item.parentID == id) hasChild = true
        if (item.id != id) temp.push(item)
      })
      if (hasChild) {
        toast('Child Tasks Exist!', { type: 'error' })
        resolve(true)
      } else {
        setTasks(temp)
        resolve(true)
      }
    })
  }

  const inputLogic = (inputs: Record<string, InputType>, ID?: number, key?: string) => {
    const templateNo = inputs['taskTemplateNo'].value
    inputs['description'].disabled = templateNo ? true : false
    if (key === 'taskTemplateNo') {
      let temp: any = taskInputs()
      Object.keys(temp).map((key) => {
        inputs[key].value = temp[key].value
      })

      tempTasks.map((item) => {
        if (item.no == templateNo) {
          Object.keys(inputs).map((key) => {
            if (!['taskTemplateNo', 'parentID', 'channels'].includes(key)) {
              inputs[key].value = item[key] || temp[key].value
              inputs[key].error = ''
            }
          })
        }
      })
    }
    return inputs
  }

  const inputELogic = (inputs: Record<string, InputType>, ID?: number, key?: string) => {
    if (key === 'type') {
      emailTemplates.map((email) => {
        if (email.id == inputs.type.value) {
          inputs.content.value = email.content
          inputs.links.value = email.links
        }
      })
    }
    return inputs
  }

  const getEmailType = (type: string) => {
    let rlt = ''
    emailTemplates.map((email) => {
      if (email.id == type) rlt = email.subject
    })
    return rlt
  }

  const renderECustomInput = (value: Record<string, any>[], onChange: Function) => {
    const onChangeLinkProps = (key: string, newValue: any, index: number) => {
      value[index][key] = newValue
      onChange(value)
    }

    const removeLink = (index: number) => {
      value.splice(index, 1)
      onChange(value)
    }

    const addLink = () => {
      if (!value) value = []
      value.push({ url: '', label: '', type: 'button' })
      onChange(value)
    }

    return (
      <div className="grid grid-cols-2 gap-4">
        {value?.map((item, index) => {
          const input: InputType = {
            inputType: 'text',
            title: '',
            value: item.url,
            prefix: 'Link:',
            indent: 12,
            showLinkDir: true,
          }
          input.additionalElements = (
            <>
              <input
                className="border-gray-300 border-[1px] px-1"
                value={item.label}
                onChange={(e: any) => onChangeLinkProps('label', e.target.value, index)}
                placeholder={`${IButtonType[item.type]} Label`}
              />
              <select
                name={`button-type-${index}`}
                id={`button-type-${index}`}
                className="py-0 pl-1 pr-6 text-[12.5px] leading-[16px] border-gray-300"
                value={item.type}
                onChange={(e) => onChangeLinkProps('type', e.target.value, index)}
              >
                {Object.keys(IButtonType).map((key) => {
                  return (
                    <option value={key} key={key}>
                      {IButtonType[key]}
                    </option>
                  )
                })}
              </select>
              <span className="cursor-pointer text-red-800" onClick={() => removeLink(index)}>
                <TrashIcon className="w-4 h-4" />
              </span>
            </>
          )
          return (
            <RenderInput
              input={input}
              Key={`url-${index}`}
              onChange={(key: string, value: any) => onChangeLinkProps('url', value, index)}
            />
          )
        })}
        <div className="col-span-full">
          <Button link onClick={addLink}>
            + Add Button & Link
          </Button>
        </div>
      </div>
    )
  }

  const renderAdditionalActions = (row: Record<string, any>) => {
    return (
      <div className="flex gap-1">
        <div
          className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded w-fit"
          onClick={() => {
            setShowModal(true)
            setPreviewData(row)
          }}
        >
          <EyeIcon className="w-4 h-4" />
        </div>
      </div>
    )
  }

  const previewHtml = useMemo(() => {
    if (!previewData) return ''
    const elementList: any[] = []
    previewData.includes?.map((elementKey: string) => {
      elementList.push({
        type: 'element3',
        data: [elementKey, `{{${elementKey}}}`],
      })
    })

    const message = getEmailHtml(
      [
        ...elementList,
        {
          type: 'element2',
          data: [
            `<div style='font-family:sans-serif; text-align: left'>${(previewData.content || '')
              .split('\n')
              .join('<br/>')}</div>`,
          ],
        },
        ...(previewData.links || []).map((link: any) => {
          return {
            type: link.type === 'button' ? 'element6' : 'link',
            data: [link.url, link.label],
          }
        }),
      ],
      originalTemplate,
    )
    return message
  }, [previewData])

  return (
    <div className="w-full relative">
      <LayoutLoading show={isLoading} />
      <div className="text-shade-blue flex items-center mb-2 font-bold">
        <ChevronLeftIcon className="w-4 h-4" />
        <span className="hover:underline cursor-pointer" onClick={() => onBack(null)}>
          Back
        </span>
      </div>

      <div className="flex flex-col gap-4 mb-4">
        <p className="text-[18px] font-semibold">Action: {label}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.keys(inputs).map((key) => {
            const input = inputs[key]
            if (input.visible === false) return null

            let rlt = []
            rlt.push(
              <div className={input.span ? `col-span-${input.span}` : ''} key={key}>
                <RenderInput input={input} Key={key} onChange={onChange} />
              </div>,
            )
            if (key === 'section2') {
              rlt.push(
                <div key={`email-table`} className={`col-span-2`}>
                  <FormTable
                    key="emails"
                    header={[
                      {
                        key: 'emailTo',
                        title: 'To',
                        value: (row: any) => {
                          return (
                            <div>
                              {row.emailTo.map((email: string) => (
                                <div>{assigneeOptions[email]}</div>
                              ))}
                            </div>
                          )
                        },
                      },
                      {
                        key: 'content',
                        title: (
                          <div>
                            <div className="mb-1 border-b">Subject</div>
                            <div className="mb-1 border-b">Content</div>
                            <div>Loan Info</div>
                          </div>
                        ),
                        value: (row: any) => {
                          return (
                            <div>
                              <div className="mb-1 border-b">{getEmailType(row.type) || row.type}</div>
                              <div className="mb-1 border-b italic">
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: row.content?.replace(/\n/g, '<br />'),
                                  }}
                                />
                              </div>
                              <div>{row.includes?.map((key: string) => `${eInputs['includes'].options[key]}, `)}</div>
                            </div>
                          )
                        },
                      },
                    ]}
                    inputs={eInputs}
                    data={emails}
                    inputLogic={inputELogic}
                    onSubmit={(id, values) => onSubmitEmails(id, values)}
                    onRemove={(id) => onRemoveEmails(id)}
                    renderAdditionalActions={renderAdditionalActions}
                  />
                </div>,
              )
            }
            if (key === 'section3') {
              rlt.push(
                <div key={`section3-table`} className={`col-span-2`}>
                  <FormTable
                    key="tasks"
                    header={[
                      { key: 'parentID', title: 'Parent ID' },
                      { key: 'taskTemplateNo', title: 'Template No' },
                      {
                        key: 'description',
                        title: (
                          <div>
                            <div className="border-b mb-1">Description</div>Notes
                          </div>
                        ),
                        value: (row: any) => {
                          return (
                            <div>
                              <div className="border-b mb-1">{row.description}</div>
                              <div>{row.notes}</div>
                            </div>
                          )
                        },
                      },
                      {
                        key: 'assignedTo',
                        title: (
                          <div>
                            <div className="border-b mb-1">Assigned to</div>Manager
                          </div>
                        ),
                        value: (row: any) => {
                          return (
                            <div>
                              <div className="border-b mb-1">
                                {row.assignedTo.map((to: string) => TaskAssigneeOptions()[to] || to).join(', ')}
                              </div>
                              <div>
                                {row.assignedToManager.map((to: string) => TaskAssigneeOptions()[to] || to).join(', ')}
                              </div>
                            </div>
                          )
                        },
                      },
                      { key: 'dueDays', title: 'Due Days' },
                    ]}
                    inputs={tInputs}
                    inputLogic={inputLogic}
                    data={tasks}
                    onSubmit={(id, values) => onSubmitTasks(id, values)}
                    onRemove={(id) => onRemoveTasks(id)}
                    noText="ID"
                  />
                </div>,
              )
            }
            return rlt
          })}
        </div>
      </div>

      <Button onClick={onSubmit}>Save</Button>
      {showModal && previewData && (
        <Modal
          title={'Email Preview'}
          isOpen
          onClose={() => {
            setShowModal(false)
            setPreviewData(null)
          }}
          titleOkay=""
          titleCancel=""
        >
          <div dangerouslySetInnerHTML={{ __html: previewHtml || '' }}></div>
        </Modal>
      )}
    </div>
  )
}
